import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Badge from '../ui/badge/Badge';

interface RolePermissionsData {
    cms_roles: string[];
    client_roles: string[];
    cms_permissions: string[];
    client_permissions: string[];
}

export default function RolesPermissions() {
    const [data, setData] = useState<RolePermissionsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Edit state
    const [editingRole, setEditingRole] = useState<{ name: string; type: 'cms' | 'client' } | null>(null);
    const [rolePermissions, setRolePermissions] = useState<string[]>([]);
    const [savingPermissions, setSavingPermissions] = useState(false);
    const [loadingPermissions, setLoadingPermissions] = useState(false);

    const [formData, setFormData] = useState({
        role: '',
        type: 'cms' as 'cms' | 'client'
    });

    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await client.getApiUserPermissions() as any;
            const fetchedData = res.data?.data || res.data;
            setData(fetchedData);
        } catch (err) {
            console.error('Failed to fetch roles and permissions:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form data
        if (!formData.role || !formData.role.trim()) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Role name is required',
                icon: 'warning'
            });
            return;
        }

        try {
            // Explicitly construct payload with all required fields
            const payload = {
                role: formData.role.trim(),
                type: formData.type
            };

            await client.postApiUserRoleCreate({
                postApiUserRoleCreateRequest: payload
            });

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Role created successfully.',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
            setIsCreateModalOpen(false);
            setFormData({ role: '', type: 'cms' });
            refreshData();
        } catch (err: any) {
            console.error('Failed to create role:', err);
            const errorMessage = err.response?.data?.messages || err.response?.data?.message || err.message || 'Failed to create role';
            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });
        }
    };

    const handleDelete = async (role: string, type: 'cms' | 'client') => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Are you sure you want to delete the role "${role}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postApiUserRoleDelete({
                postApiUserRoleDeleteRequest: {
                    role: role,
                    type: type
                }
            });
            Swal.fire('Deleted!', 'Role has been deleted.', 'success');
            refreshData();
        } catch (err) {
            console.error('Failed to delete role:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete role',
                icon: 'error'
            });
        }
    };

    const handleEditPermissions = async (role: string, type: 'cms' | 'client') => {
        if (role === 'admin') {
            Swal.fire({
                title: 'System Role',
                text: 'The "admin" role permissions are managed by the system and cannot be modified.',
                icon: 'info'
            });
            return;
        }
        setEditingRole({ name: role, type });
        setRolePermissions([]);
        setIsEditModalOpen(true);
        setLoadingPermissions(true);
        try {
            // Explicitly pass type in params to ensure it reaches the backend via query string for GET
            const res = await client.getApiUserPermissionsRole({
                role,
                getApiUserPermissionsRoleRequest: { type }
            }, {
                params: { type }
            }) as any;

            const fetchedData = res.data?.data || res.data;
            const roleData = Array.isArray(fetchedData) ? fetchedData[0] : fetchedData;
            const permissionsKey = `${type}_permissions`;
            const permissions = (roleData?.[permissionsKey] || []).map((p: any) =>
                typeof p === 'string' ? p : p.name
            );

            setRolePermissions(permissions);
        } catch (err) {
            console.error('Failed to fetch role permissions:', err);
            Swal.fire('Error', 'Failed to fetch permissions for this role.', 'error');
        } finally {
            setLoadingPermissions(false);
        }
    };

    const handleTogglePermission = (permission: string) => {
        setRolePermissions(prev =>
            prev.includes(permission)
                ? prev.filter(p => p !== permission)
                : [...prev, permission]
        );
    };

    const handleSavePermissions = async () => {
        if (!editingRole) return;
        setSavingPermissions(true);
        try {
            // Ensure permissions is an array (which it should be) and other fields are explicitly present
            const payload = {
                role: editingRole.name,
                type: editingRole.type,
                permissions: rolePermissions || []
            };

            await client.postApiUserRoleUpdateRole({
                role: editingRole.name,
                postApiUserRoleUpdateRoleRequest: payload
            }, {
                // Some backends might expect these in query params for path resolution/validation
                params: {
                    type: editingRole.type,
                    role: editingRole.name
                }
            });
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Permissions updated successfully.',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
            setIsEditModalOpen(false);
            setEditingRole(null);
        } catch (err: any) {
            console.error('Failed to update permissions:', err);
            const errorMessage = err.response?.data?.messages || err.message || 'Failed to update permissions';
            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });
        } finally {
            setSavingPermissions(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white">Roles & Permissions</h2>
                    <p className="text-sm text-gray-500">Manage system access levels for administrators and clients.</p>
                </div>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium shadow-md shadow-brand-500/10"
                >
                    Create New Role
                </button>
            </div>

            {loading && !data ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* CMS Roles */}
                    <RoleSection
                        title="CMS Roles (Admin Panel)"
                        roles={data?.cms_roles || []}
                        type="cms"
                        onEdit={handleEditPermissions}
                        onDelete={handleDelete}
                    />

                    {/* Client Roles */}
                    <RoleSection
                        title="Client Roles (Storefront)"
                        roles={data?.client_roles || []}
                        type="client"
                        onEdit={handleEditPermissions}
                        onDelete={handleDelete}
                    />
                </div>
            )}

            {/* Create Role Modal */}
            <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} className="max-w-md p-0 overflow-hidden">
                <div className="flex flex-col h-full">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Create New Role</h3>
                    </div>

                    <div className="p-6 overflow-y-auto">
                        <form id="create-role-form" onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Role Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    required
                                    value={formData.role}
                                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20"
                                    placeholder="e.g. Editor"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Role Type</label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20"
                                >
                                    <option value="cms">CMS (Admin)</option>
                                    <option value="client">Client (Front)</option>
                                </select>
                            </div>
                        </form>
                    </div>

                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsCreateModalOpen(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="create-role-form"
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95"
                        >
                            Create Role
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Edit Permissions Modal */}
            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} className="max-w-2xl p-0 overflow-hidden">
                <div className="flex flex-col h-full max-h-[90vh]">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Manage Permissions: {editingRole?.name}</h3>
                        <p className="text-sm text-gray-500 capitalize">{editingRole?.type} Guard</p>
                    </div>

                    <div className="p-6 overflow-y-auto flex-grow bg-gray-50/30 dark:bg-black/20 min-h-[300px] flex flex-col">
                        {loadingPermissions ? (
                            <div className="flex-grow flex items-center justify-center">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-1">
                                {(editingRole?.type === 'cms' ? data?.cms_permissions : data?.client_permissions)?.map(permission => (
                                    <label key={permission} className="flex items-center gap-3 p-3 border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 rounded-xl hover:border-brand-500/50 hover:bg-brand-50/10 dark:hover:bg-brand-500/5 cursor-pointer transition-all">
                                        <input
                                            type="checkbox"
                                            checked={rolePermissions.includes(permission)}
                                            onChange={() => handleTogglePermission(permission)}
                                            className="w-4 h-4 rounded text-brand-500 focus:ring-brand-500"
                                        />
                                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 capitalize">
                                            {permission.replace(/_/g, ' ')}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsEditModalOpen(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSavePermissions}
                            disabled={savingPermissions}
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 disabled:opacity-50 flex items-center gap-2 transition-all active:scale-95"
                        >
                            {savingPermissions ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

function RoleSection({ title, roles, type, onEdit, onDelete }: {
    title: string;
    roles: string[];
    type: 'cms' | 'client';
    onEdit: (role: string, type: 'cms' | 'client') => void;
    onDelete: (role: string, type: 'cms' | 'client') => void;
}) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-white/[0.02] flex items-center justify-between">
                <h3 className="font-bold text-gray-800 dark:text-white uppercase text-xs tracking-wider">{title}</h3>
                <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[10px] font-bold text-gray-500 uppercase">{roles.length} Roles</span>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start text-xs font-bold uppercase text-gray-400">Role Name</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end text-xs font-bold uppercase text-gray-400">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {roles.length === 0 ? (
                            <TableRow><TableCell colSpan={2} className="px-5 py-4 text-center text-gray-500 italic">No roles defined.</TableCell></TableRow>
                        ) : (
                            roles.map(role => (
                                <TableRow key={role} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.01]">
                                    <TableCell className="px-5 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-gray-700 dark:text-gray-200">{role}</span>
                                            {role === 'admin' && <Badge size="sm" color="success">System</Badge>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-end">
                                        <div className="flex justify-end gap-3">
                                            <button
                                                onClick={() => onEdit(role, type)}
                                                className={`text-sm font-bold px-3 py-1 rounded-lg transition-colors ${role === 'admin'
                                                    ? 'text-gray-400 bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                                                    : 'text-brand-500 hover:text-brand-600 bg-brand-50 dark:bg-brand-500/10'
                                                    }`}
                                                title={role === 'admin' ? "System roles cannot be modified" : "Manage Permissions"}
                                            >
                                                Permissions
                                            </button>
                                            {role !== 'admin' && (
                                                <button
                                                    onClick={() => onDelete(role, type)}
                                                    className="text-red-500 hover:text-red-700 transition-colors"
                                                    title="Delete Role"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
